import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { usePasswordChecklist } from 'features/signUp/hooks/usePasswordChecklist';
import { isValidEmailAddress } from 'utils/inputProcessingUtils/inputProcessingUtils';
import { SignupFormData } from 'features/signUp/types';

export const useAccountCredentialsStep = () => {
  const {
    formState: { isValid },
    getFieldState,
    getValues,
    trigger,
    watch,
  } = useFormContext<SignupFormData>();
  const { translation } = useTranslationContext();
  const emptyFieldErrorMessage = translation('empty_field_error');
  const invalidMemberEmailErrorMessage = translation('invalid_email_error');
  const mismatchedMemberEmailsErrorMessage = translation('email_confirmation_error');
  const mismatchedPasswordsErrorMessage = translation('password_confirmation_error');

  // Get the current password value for validation
  const passwordValue = watch('password') || '';

  // Get the state of the password field
  const passwordFieldState = getFieldState('password');

  // Use our password checklist hook to validate the password
  const { validationResults, validatePassword } = usePasswordChecklist(
    passwordValue,
    passwordFieldState.isTouched, // Show error only if the field is touched
  );

  const memberEmail = useController<SignupFormData>({
    name: 'memberEmail',
    rules: {
      required: emptyFieldErrorMessage,
      validate: (input: string | boolean | undefined) =>
        (typeof input === 'string' && isValidEmailAddress(input)) || invalidMemberEmailErrorMessage,
    },
  });

  const memberConfirmedEmail = useController<SignupFormData>({
    name: 'memberConfirmedEmail',
    rules: {
      validate: (input: string | boolean | undefined) =>
        input === getValues('memberEmail') || mismatchedMemberEmailsErrorMessage,
    },
  });

  const password = useController<SignupFormData>({
    name: 'password',
    rules: {
      validate: (input: string | boolean | undefined) =>
        typeof input === 'string' && validatePassword(input),
    },
  });

  const confirmedPassword = useController<SignupFormData>({
    name: 'confirmedPassword',
    rules: {
      validate: (input: string | boolean | undefined) =>
        input === getValues('password') || mismatchedPasswordsErrorMessage,
    },
  });

  useEffect(() => {
    if (getFieldState('memberConfirmedEmail').isDirty) {
      trigger('memberConfirmedEmail');
    }
  }, [getValues('memberEmail')]);

  useEffect(() => {
    if (getFieldState('confirmedPassword').isDirty) {
      trigger('confirmedPassword');
    }
  }, [getValues('password')]);

  return {
    inputController: {
      memberEmail,
      memberConfirmedEmail,
      password,
      confirmedPassword,
    },
    passwordValidation: {
      results: validationResults,
    },
    canSubmit: isValid,
  };
};
